import React, { useRef, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Container, Grid, Typography, Box, List, ListItem, ListItemAvatar, Avatar, ListItemText, Divider } from '@mui/material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import TimelineDot from '@mui/lab/TimelineDot';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import LaptopMacIcon from '@mui/icons-material/LaptopMac';
import HotelIcon from '@mui/icons-material/Hotel';
import RepeatIcon from '@mui/icons-material/Repeat';
import {
    Card,
    CardContent,
    LinearProgress,
} from "@mui/material";
import { styled } from "@mui/system";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from "recharts";
import { Book, HistoryEdu, Language, School, Science } from "@mui/icons-material";
import LearningProgress from "./LearningProgress";

const useStyles = makeStyles((theme) => ({
    root: {
        fontFamily: 'montserrat',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        // marginTop: 20,
    },
    container: {
        width: '35%',
        height: 230,
        position: 'relative',
        '&:first-child': {
            marginLeft: 20,
        },
        '&:last-child': {
            marginRight: 20,
        },
    },
    trapezoidContainer: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: 300,
    },
    leaderboardContainer: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    scoreBox: {
        padding: '5px 10px',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        borderRadius: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 20,
    },
    scoreText: {
        fontSize: 10,
        fontWeight: 'bold',
        color: 'white',
        marginLeft: 5,
    },
    playerName: {
        fontSize: 14,
        fontWeight: 'bold',
        color: 'black',
        marginBottom: 15,
    },
    avatar: {
        width: 50,
        height: 50,
        borderRadius: '50%',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    leaderboardNumber: {
        fontSize: 30,
        color: 'white',
        fontWeight: 'bold',
        textShadow: '2px 2px 2px rgba(0, 0, 0, 0.4), -2px -2px 2px rgba(255, 255, 255, 0.7)',
    },
    roundedTopDiv: {
        borderTopLeftRadius: 15,
        borderTopRightRadius: 15,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', // Adjust shadow as needed
        backgroundColor: '#ffffff', // Adjust background color as needed
        /* Additional styles for content, width, height, etc. */
    },
    header: {
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        borderRadius: 20,
        padding: '7.5px 10px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 10,
    },
    headerText: {
        fontSize: 12,
        fontWeight: 'bold',
        color: 'white',
    },
    listItem: {
        margin: '0 20px',
        display: 'flex',
        alignItems: 'center',
    },
    avatarList: {
        borderRadius: '50%',
        marginRight: 10,
    },
    scoreContainer: {
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        borderRadius: 20,
        padding: '5px 7.5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    scoreText: {
        fontSize: 10,
        fontWeight: 'bold',
        color: 'white',
        marginLeft: 5,
    },
    selfRank: {
        height: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
        backgroundColor: '#175BD8',
        position: 'fixed',
        bottom: 0,
        zIndex: 1000, // Ensure it appears above other content
    },
    rootTimeline: {
        padding: theme.spacing(2),
        marginTop: theme.spacing(2),
        marginRight: theme.spacing(2),
        marginLeft: theme.spacing(2),
        width: '50%',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', // Adjust shadow as needed
        borderRadius: 8,
        background: `linear-gradient(to bottom, rgba(56, 58, 71, 0.2), ${theme.palette.primary.main})`,
    },
    imageContainer: {
        padding: 5,
        backgroundColor: 'white',
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
        borderRadius: 20,
        width: 80, // Adjust the width and height as per your requirement
        height: 80, // Ensure width and height are equal for square aspect ratio
    },
    image: {
        height: 40,
        width: 40,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
    },
}));

const buttonStyle = {
    padding: '10px',
    borderRadius: '20px',
    textAlign: 'center',
    margin: '10px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 5,
    clipBehavior: 'clip',
    backgroundColor: '#175BD8',
    fontFamily: "Noto Sans"
};

const textStyle = (isSelected) => ({
    fontSize: '14px',
    fontWeight: 'bold',
    fontFamily: "Noto Sans",
    color: isSelected ? 'white' : '#175BD8',
});

const LeaderBoard = ({
    onCourseClick,
    onCourseDetailClick
}) => {
    const classes = useStyles();
    const [leaderboard, setLeaderboard] = useState([]);
    const [currentEmployeeRank, setCurrentEmployeeRank] = useState('');
    const [currentEmployeeScore, setCurrentEmployeeScore] = useState('');
    const [name, setName] = useState('');
    const [scoreHistory, setHistoryData] = useState([]);
    const [avatar, setAvatar] = useState('');
    const [isCompany, setIsCompany] = useState(false);
    const [awarnessCoursesAll, setAwarnessCoursesAll] = useState([]);
    const [awarnessCourses, setAwarnessCourses] = useState([]);
    const [dataGrades, setDataGrades] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchAverageMarks = async () => {
            const token = localStorage.getItem('token') || '';
            const userId = localStorage.getItem('userId') || '';
            try {

                const requestOptions = {
                    method: 'GET',
                    headers: {
                        'Authorization': token,
                        'Content-Type': 'application/json'
                    },
                };
                const response = await fetch(`https://beiguru.co.in/average-marks/${userId}`, requestOptions);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const result = await response.json();
                console.log(result.data);
                const formattedData = result.data.map((item) => ({
                    name: item.courseName.slice(0, 8) || "Unknown Course", // Course name
                    grade: item.averageMarks, // Average marks
                }));
                setDataGrades(formattedData);
                setLoading(false);
            } catch (err) {
                setError("Failed to fetch data");
                setLoading(false);
            }
        };

        fetchAverageMarks();
    }, []);

    const dummyData = {
        user: {
            firstName: 'Prasun ',
            lastName: 'Ranjan',
            email: 'prasun@theinolas.com',
            usn: '673662cde900bcfdf0e8d9d3',
            batch: 'Class 1',
            elective: 'Inolas Public School',
            attendance: '0',
            completedCourses: 0,
            inProgressCourses: 0,
        },
        activities: [
        ],
    };

    useEffect(() => {
        getCourses();
    }, []);

    const handleCompanyClick = () => {
        setIsCompany(true);
        fetchLeaderBoard();
    };

    const handleDepartmentClick = () => {
        setIsCompany(false);
        fetchLeaderBoardDepartment();
    };

    const fetchLeaderBoard = async () => {
        setLeaderboard([]);

        const token = localStorage.getItem('token') || '';
        const company = localStorage.getItem('company');
        const userId = localStorage.getItem('userId');
        const firstName = localStorage.getItem('firstName');
        const avatar = localStorage.getItem('avatar');

        const headers = {
            'Content-Type': 'application/json',
            'Authorization': token,
        };

        const url = `https://beiguru.co.in/api/leaderboard/${company}/${userId}`;

        try {
            const response = await fetch(url, { headers });

            if (response.status === 200) {
                const result = await response.json();
                console.log(result);
                setLeaderboard(result.leaderboard.map((json) => ({
                    rank: json.rank,
                    score: json.score,
                    name: json.name,
                    avatar: json.avatar
                })));
                setCurrentEmployeeRank(result.currentEmployee.rank.toString());
                setCurrentEmployeeScore(result.currentEmployee.score.toString());
                setName(`${firstName}`);
                setAvatar(avatar);
            } else {

            }
        } catch (error) {
            // Handle the error
            console.error('Error fetching leaderboard:', error);
        }
    };

    const fetchLeaderBoardDepartment = async () => {
        setLeaderboard([]);

        const token = localStorage.getItem('token') || '';
        const company = localStorage.getItem('company');
        const userId = localStorage.getItem('userId');
        const department = localStorage.getItem('departmentID');
        const firstName = localStorage.getItem('firstName');
        const avatar = localStorage.getItem('avatar');

        const headers = {
            'Content-Type': 'application/json',
            'Authorization': token,
        };

        const url = `https://beiguru.co.in/api/leaderboard-department/${company}/${userId}/${department}`;
        console.log(url);

        try {
            const response = await fetch(url, { headers });

            if (response.status === 200) {
                const result = await response.json();
                console.log(result);
                setLeaderboard(result.leaderboard.map((json) => ({
                    rank: json.rank,
                    score: json.score,
                    name: json.name,
                    avatar: json.avatar
                })));
                setCurrentEmployeeRank(result.currentEmployee.rank.toString());
                setCurrentEmployeeScore(result.currentEmployee.score.toString());
                setName(`${firstName}`);
                setAvatar(avatar);
            } else {
                // Handle the case where the response status is not 200
            }
        } catch (error) {
            // Handle the error
            console.error('Error fetching leaderboard:', error);
        }
    }

    useEffect(() => {
        fetchLeaderBoardDepartment();
        fetchHistory();
    }, []); // Empty dependency array to run once on mount


    const fetchHistory = async () => {
        try {
            const token = localStorage.getItem('token'); // Assuming token is stored in localStorage
            const userId = localStorage.getItem('userId'); // Assuming userId is stored in localStorage
            const company = localStorage.getItem('company'); // Assuming company is stored in localStorage

            const headers = {
                'Content-Type': 'application/json',
                'Authorization': token,
            };

            const url = `https://beiguru.co.in/api/score-history/${userId}/company/${company}`;
            const response = await fetch(url, { headers });

            if (!response.ok) {
                throw new Error('Failed to fetch history');
            }

            const result = await response.json();

            const formattedData = result.map(item => ({
                score: `${item.increment ? '+' : '-'}${item.score}`,
                type: item.increment ? 'increment' : 'decrement', // You can define a constant for type if needed
                action: item.action === 'level' ? 'general' : 'course', // Assuming Action is an enum in your Dart code
                title: historyTitle(item),
                subtitle: historyMessage(item),
                time: item.createdAt
            }));

            setHistoryData(formattedData);
        } catch (error) {
            console.error('Error fetching history:', error);
            // Handle error
        }
    };

    const historyMessage = (item) => {
        if (item.action === 'quiz') {
            if (item.increment) {
                return `Points gained for completing the quiz of course ${item.course.title}`;
            } else {
                return `Points deducted for giving incorrect answer of quiz of course ${item.course.title}`;
            }
        } else if (item.action === 'bonusCourse') {
            return `Bonus points gained for completing the course ${item.course.title}`;
        } else if (item.action === 'course') {
            if (item.increment) {
                return `Points gained for completing the course ${item.course.title}`;
            } else {
                return `Points deducted for not completing the course ${item.course.title}`;
            }
        } else if (item.action === 'generalQuiz') {
            if (item.score === 250) {
                return `Bonus points gained for completing the general quiz ${item.generalQuiz.name} and scoring 100%`;
            }
            if (item.score === 500) {
                return `Points gained for completing the general quiz ${item.generalQuiz.name}`;
            }
            return '';
        } else if (item.action === 'level') {
            const score = parseInt(item.score);
            if (score > 10000) {
                return 'You level has changed to Cyber SuperNova';
            } else if (score > 6000) {
                return 'You level has changed to Cyber Nebula';
            } else if (score > 4000) {
                return 'You level has changed to Cyber Star';
            } else if (score > 2000) {
                return 'You level has changed to Cyber Titan';
            } else {
                return 'You level has changed to Cyber Comet';
            }
        } else {
            return '';
        }
    };

    const historyTitle = (item) => {
        if (item.action === 'quiz') {
            if (item.increment) {
                return 'Quiz';
            } else {
                return 'Incorrect';
            }
        } else if (item.action === 'bonusCourse') {
            return 'Bonus';
        } else if (item.action === 'course') {
            if (item.increment) {
                return 'Course';
            } else {
                return 'Due Date';
            }
        } else if (item.action === 'generalQuiz') {
            if (item.score === 250) {
                return 'Bonus';
            }
            return 'General Quiz';
        } else if (item.action === 'level') {
            return 'Level';
        } else {
            return '';
        }
    };

    const filteredLeaderboard = leaderboard.filter(entry => entry.rank >= 4);

    const getLevelImage = (score) => {
        if (score > 10000) {
            return require('../assets/l5.png');
        } else if (score > 6000) {
            return require('../assets/l4.png');
        } else if (score > 4000) {
            return require('../assets/l3.png');
        } else if (score > 2000) {
            return require('../assets/l2.png');
        } else {
            return require('../assets/l1.png');
        }
    };

    const score = parseInt(currentEmployeeScore);

    const getCourses = async () => {
        try {
            const prefs = getLocalStorage();
            const userId = prefs.userId;
            const token = prefs.token;
            const parsedData = await fetchProgressData(userId, token);
            const coursesJson = await fetchCoursesData(token, prefs.company);
            console.log(coursesJson);


            const awarnessCoursesAll = [];
            for (const courseJson of coursesJson) {
                const element = parsedData.find((_element) => _element.course === courseJson._id);
                const course = {
                    id: courseJson._id,
                    title: courseJson.title,
                    description: courseJson.description,
                    thumbnail: `https://beiguru.co.in/${courseJson.thumbnail.replace(' ', '%20')}`,
                    isPopular: courseJson.isPopular,
                    continuousCourse: courseJson.continuousCourse,
                    showConsentForm: courseJson.showConsentForm,
                    isFinal: element ? element.isFinal : false,
                    isCompleted: element ? element.isCompleted : false,
                    total: element ? element.total : 0,
                    currentProgress: element ? element.currentProgress : [],
                    progressID: element ? element._id : '',
                    consentText: courseJson.consentText,
                    schedule: courseJson.schedule,
                };
                awarnessCoursesAll.push(course);
            }
            awarnessCoursesAll.sort((a, b) => a.isFinal.toString().localeCompare(b.isFinal.toString()));
            setAwarnessCoursesAll(awarnessCoursesAll);
            setAwarnessCourses(awarnessCoursesAll);
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching courses:', error);
            setIsLoading(false);
            // Handle error
        }
    };

    const getLocalStorage = () => {
        const userId = localStorage.getItem('userId') || '';
        const token = localStorage.getItem('token') || '';
        const firstName = localStorage.getItem('firstName') || '';
        const companyLogo = localStorage.getItem('companyLogo') || '';
        const company = localStorage.getItem('company') || '';
        return { userId, token, firstName, companyLogo, company };
    };


    useEffect(() => {
        const getCourses = async () => {
            try {
                const prefs = getLocalStorage();
                const userId = prefs.userId;
                const token = prefs.token;

                const parsedData = await fetchProgressData(userId, token);
                const coursesJson = await fetchCoursesData(token, prefs.company);
                const awarnessCoursesAll = [];
                for (const courseJson of coursesJson) {
                    const element = parsedData.find(
                        (_element) => _element.course === courseJson._id
                    );
                    const course = {
                        id: courseJson._id,
                        title: courseJson.title,
                        description: courseJson.description,
                        thumbnail: `https://beiguru.co.in/${courseJson.thumbnail.replace(
                            " ",
                            "%20"
                        )}`,
                        isPopular: courseJson.isPopular,
                        continuousCourse: courseJson.continuousCourse,
                        showConsentForm: courseJson.showConsentForm,
                        isFinal: element ? element.isFinal : false,
                        isCompleted: element ? element.isCompleted : false,
                        total: element ? element.total : 0,
                        currentProgress: element ? element.currentProgress : [],
                        progressID: element ? element._id : "",
                        consentText: courseJson.consentText,
                        modules: courseJson.modules,
                    };
                    awarnessCoursesAll.push(course);
                }
                awarnessCoursesAll.sort((a, b) =>
                    a.isFinal.toString().localeCompare(b.isFinal.toString())
                );
                setAwarnessCoursesAll(awarnessCoursesAll);
                setAwarnessCourses(awarnessCoursesAll);
                setIsLoading(false);
            } catch (error) {
                console.error("Error fetching courses:", error);
                setIsLoading(false);
                // Handle error
            }
        };
        getCourses();
    }, []);

    const fetchProgressData = async (userId, token) => {
        // Fetch progress data here
        const response = await fetch(`https://beiguru.co.in/api/progress/${userId}`, {
            headers: {
                Authorization: token,
            },
        });
        if (!response.ok) {
            if (response.status == 401) {
                localStorage.clear();
                window.location.reload();
                return [];
            }
        }
        return response.json();
    };

    const fetchCoursesData = async (token, company) => {
        // Fetch courses data here
        const response = await fetch(`https://beiguru.co.in/admin/courses-company-main/${company}`, {
            headers: {
                Authorization: token,
            },
        });
        if (!response.ok) {
            if (response.status == 401) {
                localStorage.clear();
                window.location.reload();
                return [];
            }
        }
        return response.json();
    };

    const getTimelineIcon = (action, type, score) => {
        switch (action) {
            case 'general':
                return <img src={getLevelImage(score)} alt="Level Icon" style={{ height: 24, width: 24 }} />;
            default:
                return getIconForType(type); // No icon for default case
        }
    };

    const getIconForType = (type) => {
        switch (type) {
            case 'increment':
                return <img src={require('../assets/checked.png')} alt="Increment Icon" style={{ height: 24, width: 24 }} />;
            case 'decrement':
                return <img src={require('../assets/unchecked.png')} alt="Decrement Icon" style={{ height: 24, width: 24 }} />;
            default:
                return null; // No icon for default case
        }
    };



    const Container = styled(Box)({
        padding: "20px",
        display: "grid",
        gap: "20px",
    });

    const Row = styled(Box)({
        display: "flex",
        gap: "20px",
    });


    const lessonData = [
        { name: "Completed", value: awarnessCoursesAll.filter(e => e.isFinal).length },
        { name: "Pending", value: awarnessCoursesAll.filter(e => !e.isFinal).length },
    ];

    const COLORS = ["#0088FE", "#FFBB28"];

    const documents = [
        "Data Not Available",
        "Data Not Available",
        "Data Not Available",
    ];

    const colors = [
        "#FFB6C1",
        "#FFD700",
        "#87CEFA",
        "#98FB98",
        "#FFA07A",
        "#DA70D6",
    ];

    const activeCourses = [
        {
            subject: "N/A",
            thumbnail: "https://via.placeholder.com/24",
            progress: 75,
            isCompleted: false,
        },
        {
            subject: "N/A",
            thumbnail: "https://via.placeholder.com/24",
            progress: 50,
            isCompleted: false,
        },
        {
            subject: "N/A",
            thumbnail: "https://via.placeholder.com/24",
            progress: 100,
            isCompleted: true,
        },

    ]


    return (
        <>
            <div className={classes.root} style={{ display: 'flex', flexDirection: 'row', fontFamily: "Noto Sans", }}>
                <div style={{ padding: '10px', flex: 2, }}>
                    <div>
                        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                            <h2 style={{ margin: '0', fontSize: '18px', fontWeight: 'bold' }}>Welcome back {localStorage.getItem('firstName')?.split(' ')[0]}, Continue learning</h2>
                            <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>BATCH: {localStorage.getItem('department')} | Organization: {localStorage.getItem('companyName')}</p>
                            <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>Roll ID: {localStorage.getItem('lastName')}</p>
                            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                                <div style={{ flex: 1, backgroundColor: '#eef3fd', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                                    <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>In Progress</p>
                                    <h3 style={{ margin: '5px 0', fontSize: '20px', color: '#007bff' }}>{awarnessCoursesAll.filter(e => !e.isFinal).length}</h3>
                                </div>
                                <div style={{ flex: 1, backgroundColor: '#eef3fd', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                                    <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>Completed</p>
                                    <h3 style={{ margin: '5px 0', fontSize: '20px', color: '#007bff' }}>{awarnessCoursesAll.filter(e => e.isFinal).length}</h3>
                                </div>
                                <div style={{ flex: 1, backgroundColor: '#eef3fd', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                                    <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>Total</p>
                                    <h3 style={{ margin: '5px 0', fontSize: '20px', color: '#007bff' }}>{awarnessCoursesAll.length}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* <div style={{ height: '89vh', overflowY: 'scroll', flex: 1, }}>
                {leaderboard.length > 2 ? <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', }}>
                    <div style={{ display: 'flex', flexDirection: 'row', height: '230px', width: '300px', position: 'relative', alignItems: 'end' }}>
                        <div style={{ flex: '1', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <TrapezoidPainterSecond width={120} height={20} />
                            <ReversedTrapezoidRankSecondPainter width={120} height={90} />
                            <div className={classes.leaderboardNumber} style={{ position: 'absolute', bottom: '20px' }}>2</div>
                            <div className={classes.scoreBox} style={{ position: 'absolute', bottom: '80px' }}>
                                <StarBorderIcon style={{ fontSize: 15, color: 'white' }} />
                                <span className={classes.scoreText}>{leaderboard[1].score}</span>
                            </div>

                            <div className={classes.playerName} style={{ position: 'absolute', bottom: '115px' }}>
                                {leaderboard[1].name}
                            </div>

                            <img
                                src={require(`../assets/a${leaderboard[1].avatar}.png`)}
                                alt={`avatar-a1`}
                                className={classes.avatar}
                                style={{ position: 'absolute', bottom: '155px' }}
                            />
                        </div>
                        <div style={{ zIndex: 111111, marginLeft: '-30px', flex: '1', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <TrapezoidPainter width={120} height={20} />
                            <ReversedTrapezoidPainter width={120} height={110} />
                            <div className={classes.leaderboardNumber} style={{ position: 'absolute', bottom: '20px' }}>1</div>
                            <div className={classes.scoreBox} style={{ position: 'absolute', bottom: '100px' }}>
                                <StarBorderIcon style={{ fontSize: 15, color: 'white' }} />
                                <span className={classes.scoreText}>{leaderboard[0].score}</span>
                            </div>

                            <div className={classes.playerName} style={{ position: 'absolute', bottom: '135px' }}>
                                {leaderboard[0].name}
                            </div>

                            <img
                                src={require(`../assets/a${leaderboard[0].avatar}.png`)}
                                alt={`avatar-a1`}
                                className={classes.avatar}
                                style={{ position: 'absolute', bottom: '175px' }}
                            />
                        </div>
                        <div style={{ flex: '1', marginLeft: '-30px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <TrapezoidPainterThird width={120} height={20} />
                            <ReversedTrapezoidRankPainter width={120} height={70} />
                            <div className={classes.leaderboardNumber} style={{ position: 'absolute', bottom: '20px' }}>3</div>
                            <div className={classes.scoreBox} style={{ position: 'absolute', bottom: '60px' }}>
                                <StarBorderIcon style={{ fontSize: 15, color: 'white' }} />
                                <span className={classes.scoreText}>{leaderboard[2].score}</span>
                            </div>
                            <div className={classes.playerName} style={{ position: 'absolute', bottom: '95px' }}>
                                {leaderboard[2].name}
                            </div>
                            <img
                                src={require(`../assets/a${leaderboard[2].avatar}.png`)}
                                alt={`avatar-a1`}
                                className={classes.avatar}
                                style={{ position: 'absolute', bottom: '135px' }}
                            />
                        </div>
                    </div>
                    <div className={classes.roundedTopDiv} style={{ width: '450px', padding: '20px', display: 'flex', flexDirection: 'column', }}>
                        <Box className={classes.header}>
                            <Typography className={classes.headerText} style={{ marginLeft: '30px', marginRight: '30px' }} variant="body1">Rank</Typography>
                            <Typography className={classes.headerText} style={{ marginLeft: '30px', marginRight: '30px' }} variant="body1" >Students</Typography>
                            <Typography className={classes.headerText} style={{ marginLeft: '30px', marginRight: '30px' }} variant="body1">Score</Typography>
                            <div style={{ width: '20px' }} />
                        </Box>

                        <List>
                            {filteredLeaderboard.filter(entry => entry.rank >= 4).map((entry, index) => (
                                <React.Fragment key={index}>
                                    <ListItem style={{ width: '410px', borderRadius: '50px' }}>
                                        Rank
                                        <Typography variant="body1" style={{ fontFamily: "Noto Sans", fontWeight: 'bold', color: 'black', marginLeft: '20px', marginRight: '20px' }}>{entry.rank}</Typography>

                                        Avatar
                                        <ListItemAvatar>
                                            <img
                                                src={require(`../assets/a${entry.avatar}.png`)}
                                                alt={`avatar-a1`}
                                                className={classes.avatar}
                                            />
                                        </ListItemAvatar>

                                        Students Info
                                        <ListItemText
                                            primary={<Typography variant="body1" style={{ fontFamily: "Noto Sans", fontWeight: 'bold', color: 'black' }}>{entry.name}</Typography>}
                                            secondary={<Typography variant="body2" style={{ fontFamily: "Noto Sans", color: 'black' }}>{entry.department}</Typography>}
                                            style={{ marginLeft: '10px', marginRight: '20px' }}
                                        />

                                        Score
                                        <Box className={classes.scoreContainer} style={{ marginLeft: '20px', marginRight: '20px' }}>
                                            <div style={{ width: '5px' }} />
                                            <StarBorderIcon style={{ color: 'white', fontSize: 15 }} />
                                            <div style={{ width: '5px' }} />
                                            <Typography className={classes.scoreText}>{entry.score}</Typography>
                                            <div style={{ width: '5px' }} />
                                        </Box>
                                    </ListItem>
                                    {index < filteredLeaderboard.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                        <ListItem style={{ width: '410px', borderRadius: '20px 20px 0px 0px', backgroundColor: '#175BD8', position: 'absolute', bottom: '0', }}>
                            <Typography variant="body1" style={{ fontWeight: 'bold', color: 'white', marginLeft: '20px', marginRight: '20px' }}>{currentEmployeeRank}</Typography>
                            <ListItemAvatar>
                                <img
                                    src={require(`../assets/a${avatar}.png`)}
                                    alt={`avatar-a1`}
                                    className={classes.avatar}
                                />
                            </ListItemAvatar>
                            <ListItemText
                                primary={<Typography variant="body1" style={{ fontFamily: "Noto Sans", fontWeight: 'bold', color: 'white' }}>{name} (You)</Typography>}
                                style={{ marginLeft: '10px', marginRight: '20px' }}
                            />

                            <Box className={classes.scoreContainer} style={{ marginLeft: '20px', marginRight: '20px' }}>
                                <div style={{ width: '5px' }} />
                                <StarBorderIcon style={{ color: 'white', fontSize: 15 }} />
                                <div style={{ width: '5px' }} />
                                <Typography className={classes.scoreText}>{currentEmployeeScore}</Typography>
                                <div style={{ width: '5px' }} />
                            </Box>
                        </ListItem>
                        <div style={{ height: `${80 * (7 - filteredLeaderboard.length)}px` }} />
                        <div style={{ height: '60px' }} />
                    </div>
                </div> : null}
            </div> */}
            </div>
            <Container>
                <Row>
                    <Card
                        sx={{ flex: 1, padding: "20px", color: "white", borderRadius: 2 }}
                    >
                        <Typography variant="h6" sx={{ mb: 2, color: "#000" }}>
                            Semester Grades
                        </Typography>
                        {loading ? (
                            <Typography sx={{ color: "#333" }}>Loading...</Typography>
                        ) : error ? (
                            <Typography sx={{ color: "red" }}>{error}</Typography>
                        ) : (
                            <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={dataGrades}>
                                    <XAxis dataKey="name" tick={{ fill: "#000" }} />
                                    <YAxis tick={{ fill: "#000" }} />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: "#fff",
                                            color: "#333",
                                        }}
                                    />
                                    <Bar
                                        dataKey="grade"
                                        fill="url(#gradient)"
                                        barSize={20}
                                        radius={[5, 5, 0, 0]}
                                    />
                                    <defs>
                                        <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#36A2EB" />
                                            <stop offset="100%" stopColor="#002979" />
                                        </linearGradient>
                                    </defs>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </Card>

                    <Card
                        sx={{ flex: 1, padding: "20px", color: "white", borderRadius: 2 }}
                    >
                        <Typography variant="h6" sx={{ mb: 2, color: "#000" }}>
                            Lesson Distribution
                        </Typography>
                        <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                                <Pie
                                    data={lessonData}
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {lessonData.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={COLORS[index % COLORS.length]}
                                        />
                                    ))}
                                </Pie>
                            </PieChart>
                            <div style={{ justifyContent: 'center', alignContent: 'center', alignItems: 'center', display: 'flex', flexDirection: 'row' }}>
                                <div style={{ height: '10px', width: '10px', backgroundColor: COLORS[0], marginRight: '5px' }} />
                                <h5 style={{ fontSize: '10px', color: 'black', fontFamily: 'Noto Sans', color: COLORS[0], marginRight: '5px', marginTop: '0', marginBottom: '0' }}>Completed</h5>
                                <div style={{ height: '10px', width: '10px', backgroundColor: COLORS[1], marginRight: '5px' }} />
                                <h5 style={{ fontSize: '10px', color: 'black', fontFamily: 'Noto Sans', color: COLORS[1], marginRight: '5px', marginTop: '0', marginBottom: '0' }}>In Progress</h5>
                            </div>
                        </ResponsiveContainer>
                    </Card>
                </Row>

                <Row>
                    <div className="col-md-6">
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Recent Upload
                        </Typography>
                        <Card
                            sx={{ flex: 1, padding: "20px", borderRadius: 2, boxShadow: 3 }}
                        >
                            {awarnessCourses.sort((a, b) => a.schedule < b.schedule).slice(0, 3).map((doc, index) => (
                                <Card
                                    key={index}
                                    sx={{
                                        margin: "2px 0",
                                        padding: "3px",
                                        boxShadow: 1,
                                        backgroundColor: colors[index % colors.length],
                                    }}
                                    onClick={() => onCourseClick(doc)}
                                >
                                    <CardContent>
                                        <Typography variant="body1">{doc.title}</Typography>
                                    </CardContent>
                                </Card>
                            ))}
                        </Card>
                    </div>

                    <div className="col-md-6">
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Active Courses Progress
                        </Typography>
                        <Card sx={{ flex: 1, padding: "20px", borderRadius: 2, boxShadow: 3 }}>
                            {awarnessCourses.filter((e) => !e.isFinal).map((course, index) => {
                                const percentCompleted = Math.min(
                                    ((course.currentProgress.length / course.total) * 100) || 0,
                                    100
                                );

                                return <Box
                                    key={index}
                                    sx={{
                                        margin: "10px 0",
                                        display: "flex",
                                        alignItems: "center",
                                        backgroundColor: "white",
                                        p: 2,
                                        borderRadius: 1,
                                        position: "relative",
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", minWidth: 120 }}>
                                        {course.thumbnail ? (
                                            <img
                                                src={course.thumbnail}
                                                alt={course.subject}
                                                style={{
                                                    width: 24,
                                                    height: 24,
                                                    marginRight: 8,
                                                    borderRadius: "50%",
                                                }}
                                            />
                                        ) : (
                                            <Book sx={{ mr: 1 }} />
                                        )}
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: "bold",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {course.title.slice(0, 8)}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ flex: 1, ml: 2 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={percentCompleted}
                                            sx={{
                                                height: 10,
                                                borderRadius: 5,
                                                backgroundColor: "#e0e0e0",
                                                "& .MuiLinearProgress-bar": {
                                                    backgroundColor: course.isCompleted ? "#4caf50" : "#2196f3",
                                                },
                                            }}
                                        />
                                    </Box>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            ml: 2,
                                            fontWeight: "bold",
                                            minWidth: "45px",
                                            color: course.isCompleted ? "#4caf50" : "inherit",
                                        }}
                                    >
                                        {Math.round(percentCompleted)}%
                                    </Typography>

                                    {course.isFinal && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                right: -8,
                                                top: -8,
                                                backgroundColor: "#4caf50",
                                                color: "white",
                                                padding: "4px 8px",
                                                borderRadius: "12px",
                                                fontSize: "0.75rem",
                                            }}
                                        >
                                            Completed
                                        </Box>
                                    )}
                                </Box>
                            })}

                            {activeCourses.length === 0 && (
                                <Typography variant="body1" sx={{ textAlign: "center", py: 2 }}>
                                    No active courses found
                                </Typography>
                            )}
                        </Card>
                    </div>
                </Row>
            </Container>
        </>
    );
};

const TrapezoidPainter = ({ width, height }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = width;
        canvas.height = height;

        // Clear the canvas
        ctx.clearRect(0, 0, width, height);

        // Create shadow
        ctx.save();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 10;

        // Draw shadow trapezoid
        ctx.beginPath();
        ctx.moveTo(10, height + 10);
        ctx.lineTo(width * 0.2 + 10, 10);
        ctx.lineTo(width * 0.8 + 10, 10);
        ctx.lineTo(width + 10, height + 10);
        ctx.closePath();
        ctx.fill();
        ctx.restore();

        // Create gradient
        const gradient = ctx.createLinearGradient(0, height, 0, 0);
        gradient.addColorStop(0, '#77A5F3');
        gradient.addColorStop(1, '#424FC9');

        // Draw trapezoid with gradient
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(0, height);
        ctx.lineTo(width * 0.2, 0);
        ctx.lineTo(width * 0.8, 0);
        ctx.lineTo(width, height);
        ctx.closePath();
        ctx.fill();
    }, [width, height]);

    return <canvas ref={canvasRef} />;
};

const TrapezoidPainterSecond = ({ width, height }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = width;
        canvas.height = height;

        // Clear the canvas
        ctx.clearRect(0, 0, width, height);

        // Create shadow
        ctx.save();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 10;

        // Draw shadow trapezoid
        ctx.beginPath();
        ctx.moveTo(10, height + 10);
        ctx.lineTo(width * 0.2 + 10, 10);
        ctx.lineTo(width * 0.8 + 10, 10);
        ctx.lineTo(width + 10, height + 10);
        ctx.closePath();
        ctx.fill();
        ctx.restore();

        // Create gradient
        const gradient = ctx.createLinearGradient(0, height, 0, 0);
        gradient.addColorStop(0, '#69D683');
        gradient.addColorStop(1, '#77BC9C');

        // Draw trapezoid with gradient
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(0, height);
        ctx.lineTo(width * 0.2, 0);
        ctx.lineTo(width * 0.8, 0);
        ctx.lineTo(width, height);
        ctx.closePath();
        ctx.fill();
    }, [width, height]);

    return <canvas ref={canvasRef} />;
};

const TrapezoidPainterThird = ({ width, height }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = width;
        canvas.height = height;

        // Clear the canvas
        ctx.clearRect(0, 0, width, height);

        // Create shadow
        ctx.save();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 10;

        // Draw shadow trapezoid
        ctx.beginPath();
        ctx.moveTo(10, height + 10);
        ctx.lineTo(width * 0.2 + 10, 10);
        ctx.lineTo(width * 0.8 + 10, 10);
        ctx.lineTo(width + 10, height + 10);
        ctx.closePath();
        ctx.fill();
        ctx.restore();

        // Create gradient
        const gradient = ctx.createLinearGradient(0, height, 0, 0);
        gradient.addColorStop(0, '#ECF9A1');
        gradient.addColorStop(1, '#C1CBA6');

        // Draw trapezoid with gradient
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(0, height);
        ctx.lineTo(width * 0.2, 0);
        ctx.lineTo(width * 0.8, 0);
        ctx.lineTo(width, height);
        ctx.closePath();
        ctx.fill();
    }, [width, height]);

    return <canvas ref={canvasRef} />;
};

const ReversedTrapezoidPainter = ({ width, height }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = width;
        canvas.height = height;

        // Clear the canvas
        ctx.clearRect(0, 0, width, height);

        // Create shadow
        ctx.save();
        ctx.fillStyle = 'transparent';
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 10;

        // Draw shadow trapezoid
        ctx.beginPath();
        ctx.moveTo(10, 10);
        ctx.lineTo(width + 10, 10); // Top right corner with offset for shadow
        ctx.lineTo(width * 0.8 + 10, height + 10); // Bottom right corner with offset for shadow
        ctx.lineTo(width * 0.2 + 10, height + 10); // Bottom left corner with offset for shadow
        ctx.lineTo(10, 10); // Top left corner with offset for shadow
        ctx.closePath();
        ctx.fill();
        ctx.restore();

        // Create gradient
        const gradient = ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, '#77A5F3');
        gradient.addColorStop(1, '#424FC9');

        // Draw trapezoid with gradient
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(width, 0); // Top right corner
        ctx.lineTo(width * 0.8, height); // Bottom right corner
        ctx.lineTo(width * 0.2, height); // Bottom left corner
        ctx.lineTo(0, 0); // Top left corner
        ctx.closePath();
        ctx.fill();
    }, [width, height]);

    return <canvas ref={canvasRef} />;
};

const ReversedTrapezoidRankPainter = ({ width, height }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = width;
        canvas.height = height;

        // Clear the canvas
        ctx.clearRect(0, 0, width, height);

        // Create shadow
        ctx.save();
        ctx.fillStyle = 'transparent';
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 10;

        // Draw shadow trapezoid
        ctx.beginPath();
        ctx.moveTo(10, 10);
        ctx.lineTo(width + 10, 10); // Top right corner with offset for shadow
        ctx.lineTo(width * 0.8 + 10, height + 10); // Bottom right corner with offset for shadow
        ctx.lineTo(10, height + 10); // Bottom left corner with offset for shadow
        ctx.closePath();
        ctx.fill();
        ctx.restore();

        // Create gradient
        const gradient = ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, '#ECF9A1');
        gradient.addColorStop(1, '#3643BD');

        // Draw trapezoid with gradient
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(width, 0); // Top right corner
        ctx.lineTo(width * 0.8, height); // Bottom right corner
        ctx.lineTo(0, height); // Bottom left corner
        ctx.closePath();
        ctx.fill();
    }, [width, height]);

    return <canvas ref={canvasRef} />;
};

const ReversedTrapezoidRankSecondPainter = ({ width, height }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = width;
        canvas.height = height;

        // Clear the canvas
        ctx.clearRect(0, 0, width, height);

        // Create shadow
        ctx.save();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 10;

        // Draw shadow trapezoid
        ctx.beginPath();
        ctx.moveTo(10, 10);
        ctx.lineTo(width + 10, 10); // Top right corner with offset for shadow
        ctx.lineTo(width + 10, height + 10); // Bottom right corner with offset for shadow
        ctx.lineTo(width * 0.2 + 10, height + 10); // Bottom left corner with offset for shadow
        ctx.closePath();
        ctx.fill();
        ctx.restore();

        // Create gradient
        const gradient = ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, '#69D683');
        gradient.addColorStop(1, '#1F34A7');

        // Draw trapezoid with gradient
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(width, 0); // Top right corner
        ctx.lineTo(width, height); // Bottom right corner
        ctx.lineTo(width * 0.2, height); // Bottom left corner
        ctx.closePath();
        ctx.fill();
    }, [width, height]);

    return <canvas ref={canvasRef} />;
};

export default LeaderBoard;